import { AdvertisementDetail } from "../interface/device.details";
import moment from "moment";

export type NoFlightsAdMode = "fullscreen" | "ratio" | "none";

export type BuildAdSequenceOptions = {
  noFlightsMode?: NoFlightsAdMode;

  /**
   * TOP-LEVEL flag on AdvertisementDetail:
   * If true AND ratio ads are active, return ratio ads only (no gaps)
   * so UI can show ratio ads side-by-side with flights.
   */
  persistentRatio?: boolean;

  /**
   * When flights exist and footer ads are active, keep showing footer ads continuously (no gaps).
   */
  persistentFooter?: boolean;
};

export const isAdActiveNow = (ad: any): boolean => {
  const now = moment();
  const localNowDate = now.format("YYYY-MM-DD");
  const dayName = now.format("ddd");

  const isWithinDateRange =
    now.isSameOrAfter(moment(ad.startAt)) &&
    now.isSameOrBefore(moment(ad.endAt).endOf("day"));

  if (!isWithinDateRange) return false;

  let startTime: string;
  let endTime: string;

  if (ad.weekdays?.length) {
    const weekday = ad.weekdays.find(
      (w: any) => w.day.toLowerCase() === dayName.toLowerCase()
    );
    if (!weekday) return false;

    startTime = weekday.startTime;
    endTime = weekday.endTime;
  } else {
    startTime = ad.startTime;
    endTime = ad.endTime;
  }

  const start = moment(`${localNowDate} ${startTime}`, "YYYY-MM-DD HH:mm");
  const end = moment(`${localNowDate} ${endTime}`, "YYYY-MM-DD HH:mm");

  return now.isBetween(start, end);
};

export const getNoFlightsAdMode = (adData?: AdvertisementDetail): NoFlightsAdMode => {
  if (!adData) return "none";

  const noFlights = !adData.flightDuration || adData.flightDuration === 0;
  if (!noFlights) return "none";

  const activeAds = (adData.ads ?? []).filter(isAdActiveNow);
  if (!activeAds.length) return "none";

  const hasFullScreen = activeAds.some(
    (a: any) => String(a.type).toLowerCase() === "fullscreen"
  );
  if (hasFullScreen) return "fullscreen";

  const hasRatioBased = activeAds.some((a: any) => !!a.ratio);
  if (hasRatioBased) return "ratio";

  return "none";
};

export const getActiveRatioAds = (adData?: AdvertisementDetail): any[] => {
  if (!adData?.ads?.length) return [];
  return adData.ads.filter((a: any) => !!a?.ratio && isAdActiveNow(a));
};

export const getActiveFooterAds = (adData?: AdvertisementDetail): any[] => {
  if (!adData?.ads?.length) return [];
  return adData.ads.filter(
    (a: any) => String(a.type).toLowerCase() === "footer" && isAdActiveNow(a)
  );
};

export const buildAdSequence = (
  adData: AdvertisementDetail,
  opts?: BuildAdSequenceOptions
): any[] => {
  const { groupedAdsCount, flightDuration, ads } = adData;

  const activeAds = (ads ?? []).filter(isAdActiveNow);

  // No active ads now => empty (so hook can return currentAd=null)
  if (!activeAds.length) return [];

  // Scenario 1: No flights + FullScreen active => ADS ONLY (no gaps)
  if (opts?.noFlightsMode === "fullscreen") {
    return activeAds.map((ad: any) => ({
      type: "ad",
      id: ad._id,
      file: ad.file,
      duration: ad.duration,
      footerContent: ad.footerContent,
      ratio: ad.ratio,
      advertisementType: ad.type,
    }));
  }

  // Flights exist + Footer ads active => Footer ADS ONLY (no gaps)
  if (opts?.persistentFooter) {
    const footerAds = activeAds.filter(
      (a: any) => String(a.type).toLowerCase() === "footer"
    );
    if (!footerAds.length) return [];
    return footerAds.map((ad: any) => ({
      type: "ad",
      id: ad._id,
      file: ad.file,
      duration: ad.duration,
      footerContent: ad.footerContent,
      ratio: ad.ratio,
      advertisementType: ad.type,
    }));
  }

  // Persistent ratio (TOP-LEVEL) => Ratio ADS ONLY (no gaps)
  if (opts?.persistentRatio) {
    const ratioAds = activeAds.filter((a: any) => !!a?.ratio);
    if (!ratioAds.length) return [];
    return ratioAds.map((ad: any) => ({
      type: "ad",
      id: ad._id,
      file: ad.file,
      duration: ad.duration,
      footerContent: ad.footerContent,
      ratio: ad.ratio,
      advertisementType: ad.type,
    }));
  }

  // --------- ORIGINAL BEHAVIOR (Ad -> gap -> Ad -> gap ...) ----------
  const sequence: any[] = [];

  const repeatMap = activeAds.map((ad: any) => ({
    id: ad._id,
    file: ad.file,
    duration: ad.duration,
    footerContent: ad.footerContent,
    ratio: ad.ratio,
    advertisementType: ad.type,
    endAt: ad.endAt,
    remaining: ad.repeat === -1 ? Infinity : ad.repeat,
    infinite: ad.repeat === -1,
  }));

  const isSingleAd = repeatMap.length === 1;
  const isGrouped = groupedAdsCount > 1;

  if (!isGrouped && isSingleAd) {
    const ad = repeatMap[0];

    if (ad.infinite) {
      sequence.push({
        type: "ad",
        id: ad.id,
        file: ad.file,
        duration: ad.duration,
        footerContent: ad.footerContent,
        ratio: ad.ratio,
        advertisementType: ad.advertisementType,
      });

      sequence.push({ type: "", duration: flightDuration });
      sequence.push({ type: "", duration: null }); // end trigger
    } else {
      while (ad.remaining > 0) {
        sequence.push({
          type: "ad",
          id: ad.id,
          file: ad.file,
          duration: ad.duration,
          ratio: ad.ratio,
          footerContent: ad.footerContent,
          advertisementType: ad.advertisementType,
        });

        ad.remaining--;

        sequence.push({
          type: "",
          duration: ad.remaining === 0 ? null : flightDuration,
        });
      }
    }
  } else if (!isGrouped && !isSingleAd) {
    let stillRepeating = true;
    while (stillRepeating) {
      stillRepeating = false;
      for (const ad of repeatMap) {
        if (ad.remaining > 0 || ad.infinite) {
          sequence.push({
            type: "ad",
            id: ad.id,
            file: ad.file,
            duration: ad.duration,
            ratio: ad.ratio,
            footerContent: ad.footerContent,
            advertisementType: ad.advertisementType,
          });

          sequence.push({ type: "", duration: flightDuration });

          if (!ad.infinite) ad.remaining--;
          stillRepeating = true;
        }
      }
    }
    sequence.push({ type: "", duration: null });
  } else if (isGrouped && isSingleAd) {
    const ad = repeatMap[0];
    while (ad.remaining > 0 || ad.infinite) {
      const group: any[] = [];

      for (let i = 0; i < groupedAdsCount; i++) {
        if (ad.remaining > 0 || ad.infinite) {
          group.push({
            type: "ad",
            id: ad.id,
            file: ad.file,
            duration: ad.duration,
            footerContent: ad.footerContent,
            ratio: ad.ratio,
            advertisementType: ad.advertisementType,
          });
          if (!ad.infinite) ad.remaining--;
        }
      }

      if (group.length > 0) sequence.push(...group);
      if (ad.remaining > 0 || ad.infinite) {
        sequence.push({ type: "", duration: flightDuration });
      } else {
        sequence.push({ type: "", duration: null });
        break;
      }
    }
  } else {
    let stillRepeating = true;

    while (stillRepeating) {
      const group: any[] = [];
      let filled = 0;
      let index = 0;

      while (filled < groupedAdsCount && index < repeatMap.length) {
        const ad = repeatMap[index];
        if (ad.remaining > 0 || ad.infinite) {
          group.push({
            type: "ad",
            id: ad.id,
            file: ad.file,
            duration: ad.duration,
            ratio: ad.ratio,
            footerContent: ad.footerContent,
            advertisementType: ad.advertisementType,
          });

          if (!ad.infinite) ad.remaining--;
          filled++;
        }
        index++;
      }

      if (group.length > 0) {
        sequence.push(...group);
        sequence.push({ type: "", duration: flightDuration });
      } else {
        stillRepeating = false;
      }
    }

    sequence.push({ type: "", duration: null });
  }

  return sequence;
};
